import { type ContentfulClientApi, type SelectFilterPaths } from 'contentful';

import { LandingPageType, NavigationItemType } from '@telialt/core-cms-content/src/models/Content';
import {
    TypeLandingFields,
    TypeLandingSkeleton,
    TypeNavigationItemFields,
    TypeNavigationItemSkeleton,
} from '@telialt/core-cms-content/src/models/Generated';
import { getContentfulClientCSR } from '@telialt/core-cms-content/src/utils/contentfulClientCSR';

type Options<Fields extends Record<string, any> = {}> = {
    select?: SelectFilterPaths<Fields, 'fields'>[];
    client: Promise<ContentfulClientApi<undefined>>;
};

const loadLandingPageData = async (
    pagePath: string[],
    options: Options<TypeLandingFields>,
): Promise<LandingPageType[]> => {
    const client = await options.client;
    const response = await client.withoutUnresolvableLinks.getEntries<TypeLandingSkeleton>({
        content_type: 'landing',
        select: options?.select || ['fields', 'sys.contentType'],
        'fields.slug[in]': pagePath,
        include: 10,
    });

    return response?.items;
};

export const loadBreadcrumbsDataFromLanding = (routes: string[], client?: Promise<ContentfulClientApi<undefined>>) =>
    loadLandingPageData(routes, {
        client: client || getContentfulClientCSR(),
        select: ['fields.breadcrumbName', 'fields.hideBreadcrumbs', 'fields.slug'],
    });

const loadNavigationItems = async (
    pagePath: string[] | null,
    options: Options<TypeNavigationItemFields>,
): Promise<NavigationItemType[]> => {
    const client = await options.client;
    const isMultiple = Array.isArray(pagePath);
    const filterByUrl = isMultiple && { 'fields.url[in]': pagePath };

    const response = await client.withoutUnresolvableLinks.getEntries<TypeNavigationItemSkeleton>({
        content_type: 'navigationItem',
        select: options.select || ['fields'],
        ...filterByUrl,
        include: 10,
    });

    return response?.items || [];
};

export const loadMinimalNavigationItems = (routes: string[], client?: Promise<ContentfulClientApi<undefined>>) =>
    loadNavigationItems(routes, {
        select: ['fields.internalName', 'fields.title', 'fields.url'],
        client: client || getContentfulClientCSR(),
    });
